import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

// external components
import { Button } from 'components/Button';
import ProductReviewCard from 'features/productReviews/components/ProductReviewCard';
// eslint-disable-next-line max-len
import ProductQuestionsAndAnswersCard from 'features/productQuestionsAndAnswers/components/ProductQuestionsAndAnswersCard';
import { utagLinkPDP } from 'features/tealium';
import { toTitleCase } from 'utilities/string';

// api
import getQuestionDetails from '../api/getQuestionDetails';
// components
import { MAX_CHAR } from '../config';

import ProductSearchUGCResultsCard from './ProductSearchUGCResultsCard';
import ProductSearchUGCArticleCard from './ProductSearchUGCArticleCard';
import ProductSearchUGCResultsDetail from './ProductSearchUGCResultsDetail';
import ProductSearchUGCResultsShowMore from './ProductSearchUGCResultsShowMore';

// styles
import './ProductSearchUGCResults.scss';

const ProductSearchUGCResults = ({
	instance,
	results,
	isPendingResults,
	isPendingShowMoreResults,
	productTitle,
	productCategory,
	handleClickShowMore,
	totalResultsCount,
	totalResultsLeft,
	handleSubmitQuestionClick,
	searchTerm = ''
}) => {

	const [ loadingDetail, setLoadingDetail ] = useState(false);
	const [ detail, setDetail ] = useState(null);
	const isQAinstance = /questionsAndAnswers/i.test(instance);
	const compontentMounted = useRef(false);
	const prevResultsRef = useRef(null);

	const handleClickDetail = async (data) => {

		// question result requires additional data to display detail view
		if (data.ugcType === 'question') {

			setLoadingDetail(true);
			try {

				const questionDetail = await getQuestionDetails(data.id);
				setDetail({ ...questionDetail, ugcType: data.ugcType });
				setLoadingDetail(false);

			} catch (e) {

				setLoadingDetail(false);

			}

		} else {

			setDetail(data);

		}

		if (isQAinstance) {

			utagLinkPDP('TurnTo')({
				eventAction: 'Q&A-Open-Result',
				eventLabel: data.ugcType === 'article' ? 'Knowledge base article' : toTitleCase(data.ugcType)
			});

		}

	};

	// hide details view when user starts searching again
	useEffect(() => {

		if (!compontentMounted.current) {

			compontentMounted.current = true;
			return;

		}

		setDetail(null);

	}, [ results ]);

	useEffect(() => {

		if (!isPendingResults && searchTerm.length) {

			if (instance === 'questionsAndAnswers') {

				utagLinkPDP('TurnTo')({
					eventAction: 'Q&A-Search-Questions',
					eventLabel: `${searchTerm.toLowerCase()}-${totalResultsCount}`
				});

			} else if (instance === 'reviews') {

				utagLinkPDP('TurnTo')({
					eventAction: 'Submit-Review-Search',
					eventLabel: `${searchTerm.toLowerCase()}-${totalResultsCount}`
				});

			}

		}

	}, [ isPendingResults ]);

	const renderDetail = () => {

		const { ugcType: detailType } = detail;
		let detailProps;

		switch (detailType.toLowerCase()) {

			case 'question':
				detailProps = {
					answers: detail.answers,
					answerCount: detail.answerCount,
					dateCreated: detail.dateCreated,
					id: detail.id,
					isStaff: detail.isStaff,
					text: detail.text,
					upVotes: detail.upVotes,
					user: detail.user
				};
				return (
					<ProductSearchUGCResultsDetail
						type={detailType.toLowerCase()}
						component={(
							<ProductQuestionsAndAnswersCard
								question={detailProps}
								showAnswers
								showBestAnswer={false}
								styleVariant="grey"
								fromSearch
							/>
						)}
						qaInstance={isQAinstance}
					/>
				);
			case 'article':
				detailProps = {
					text: detail.text,
					title: detail.title
				};
				return (
					<ProductSearchUGCResultsDetail
						type={detailType.toLowerCase()}
						component={<ProductSearchUGCArticleCard article={detailProps} />}
						qaInstance={isQAinstance}
					/>
				);
			case 'review':
				detailProps = {
					downVotes: detail.downVotes,
					id: detail.id,
					media: detail.media,
					purchaseDate: detail.purchaseDate,
					rating: detail.rating,
					responses: detail.responses,
					dateCreated: detail.dateCreated,
					text: detail.text,
					title: detail.title,
					upVotes: detail.upVotes,
					user: detail.user,
					syndication: detail.syndication
				};
				if (prevResultsRef.current && Array.isArray(prevResultsRef.current)) {

					const updatedReviewFromRef = prevResultsRef.current.find((result) => result.id === detailProps.id);
					if (updatedReviewFromRef) {

						detailProps.upVotes = updatedReviewFromRef.upVotes;
						detailProps.downVotes = updatedReviewFromRef.downVotes;

						prevResultsRef.current = null;

					}

				}
				return (
					<ProductSearchUGCResultsDetail
						type={detailType.toLowerCase()}
						component={(
							<ProductReviewCard
								review={detailProps}
								fromSearch
								onVoteUpdate={({ reviewId, upVotes, downVotes }) => {

									prevResultsRef.current = results.map((result) => {

										if (result.ugcType === 'review' && result.id === reviewId) {

											return {
												...result,
												upVotes,
												downVotes
											};

										}
										return result;

									});

								}}
							/>
						)}
						qaInstance={isQAinstance}
					/>
				);
			default:
				// we should never get here, TODO: add a fallback component or error message
				console.error('Invalid UGC type. Unable to render detail.');
				return '';

		}

	};

	const renderResults = (resultData) => resultData.map((result) => (
		<ProductSearchUGCResultsCard
			key={result.id}
			detail={result}
			productTitle={productTitle}
			productCategory={productCategory}
			handleClickDetail={handleClickDetail}
			disabled={loadingDetail}
			searchTerm={searchTerm}
		/>
	));

	return (
		<section className="ProductSearchUGCResults">
			{loadingDetail && (
				<div className="ProductSearchUGCResults__loadingScreen">
					<div className="ProductSearchUGCResults__spinner lpIcon-loading" aria-hidden="true" />
				</div>
			)}
			<div
				className={classNames('ProductSearchUGCResults__header', {
					'ProductSearchUGCResults__header--noResults': results.length === 0 && isQAinstance,
					hidden: results.length === 0 && !isQAinstance
				})}
			>
				{!detail && !isPendingResults && results.length > 0 && (
					<div className="ProductSearchUGCResults__count">
						{`${totalResultsCount} ${totalResultsCount === 1 ? 'Result' : 'Results'}`}
					</div>
				)}
				{/* Back button */}
				{Boolean(detail) && results.length > 0 && (
					<Button
						className="ProductSearchUGCResults__backButton"
						type="button"
						buttonStyleVariant="tertiary"
						onClick={() => setDetail(null)}
					>
						<span className="ProductSearchUGCResults__backButtonCaret">&lt;</span>
						<span>Back</span>
					</Button>
				)}
				{isQAinstance && (
					<div className="ProductSearchUGCResults__submitNewQuestion">
						{!detail && !isPendingResults && results.length > 0 && (
							<span className="ProductSearchUGCResults__submitNewQuestionSep">|</span>
						)}
						<div>
							<Button
								onClick={() => {

									if (typeof handleSubmitQuestionClick === 'function') {

										handleSubmitQuestionClick();

									}

								}}
								type="button"
								buttonStyleVariant="link"
								className="ProductSearchUGCResults__submitNewQuestionBtn"
								disabled={searchTerm.length > MAX_CHAR}
							>
								Submit your question
							</Button>
							<span>.</span>
						</div>
					</div>
				)}
			</div>
			{!detail && (
				<div className="ProductSearchUGCResults__content">
					{!isPendingResults && results.length > 0 && (
						<div className="ProductSearchUGCResults__list">
							{renderResults(results)}
							{totalResultsLeft > 0 && (
								<ProductSearchUGCResultsShowMore
									count={totalResultsLeft}
									handleClickShowMore={handleClickShowMore}
									disabled={isPendingShowMoreResults}
								/>
							)}
						</div>
					)}
					{!isPendingResults && results.length === 0 && (
						<div className="ProductSearchUGCResults__noResults">
							<>
								<div className="ProductSearchUGCResults__noResultsHeading h4">No Results Found</div>
								<div>
									{isQAinstance
										? 'Revise your search or submit a new question'
										: 'Please try a different search word or phrase'}
								</div>
							</>
						</div>
					)}
					{isPendingResults && (
						<div className="ProductSearchUGCResults__noResults">
							<div className="ProductSearchUGCResults__spinner lpIcon-loading" aria-hidden="true" />
						</div>
					)}
				</div>
			)}
			{Boolean(detail) && renderDetail()}
		</section>
	);

};

ProductSearchUGCResults.propTypes = {
	instance: PropTypes.oneOf([ 'questionsAndAnswers', 'reviews' ]).isRequired,
	results: PropTypes.arrayOf(PropTypes.any).isRequired,
	isPendingResults: PropTypes.bool.isRequired,
	isPendingShowMoreResults: PropTypes.bool.isRequired,
	productTitle: PropTypes.string.isRequired,
	productCategory: PropTypes.string.isRequired,
	handleClickShowMore: PropTypes.func.isRequired,
	totalResultsCount: PropTypes.number.isRequired,
	totalResultsLeft: PropTypes.number.isRequired,
	handleSubmitQuestionClick: PropTypes.func,
	searchTerm: PropTypes.string
};

export default ProductSearchUGCResults;
